import { useCallback, useEffect, useState } from "react";

/**
 * localStorage-backed useState. Reads once on mount (synchronous),
 * writes through on every state change. Safe against private-mode
 * throws and SSR contexts — all storage access is wrapped in try/catch
 * and gates on `typeof window`.
 *
 * The Storage API can throw (private mode, quota, disabled) so we
 * degrade to in-memory state silently. Callers get the same API
 * either way.
 */
export function useLocalStorageState<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return initial;
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* private mode / quota — degrade to in-memory */
    }
  }, [key, value]);

  const set = useCallback(
    (next: T | ((prev: T) => T)) => setValue(next),
    [],
  );

  return [value, set];
}
